import type { SessionUser } from 'src/features/auth/types';
import { round } from 'src/features/payments/paymentUtils';
import type { Service } from 'src/features/services/types';
import {
  addDays,
  formatDateKey as formatKey,
  minutesBetween,
  weekdayOf,
  zonedDateKey,
  zonedMinutes,
} from 'src/shared/utils/datetime';
import type { Appointment, AppointmentServiceInput, AppointmentStatus } from './types';

export type CalendarView = 'day' | 'week' | 'month';

export const STATUS_LABELS: Record<AppointmentStatus, string> = {
  scheduled: 'Agendada',
  confirmed: 'Confirmada',
  completed: 'Completada',
  cancelled: 'Cancelada',
  no_show: 'No asistió',
};

/**
 * The CSS modifier each status is painted with (`v-status--<tone>`).
 *
 * One map for every place a status is drawn — the badge, the grid block, the
 * timeline card and the month chip — so a glance at the week reads the same
 * way as a glance at the drawer. The colours themselves are tokens in
 * `grazia/_tokens.scss`.
 */
export const STATUS_TONES: Record<AppointmentStatus, string> = {
  scheduled: 'scheduled',
  confirmed: 'confirmed',
  completed: 'completed',
  cancelled: 'cancelled',
  no_show: 'no-show',
};

export const statusClass = (status: AppointmentStatus): string =>
  `v-status--${STATUS_TONES[status]}`;

/**
 * Which buttons the detail drawer offers, mirroring `ALLOWED_TRANSITIONS`.
 *
 * A copy of the server's state machine, and only for deciding what to *show* —
 * the server refuses an illegal move regardless. Offering a button that always
 * answers 422 is worse than not offering it.
 */
export const NEXT_STATUSES: Record<AppointmentStatus, AppointmentStatus[]> = {
  scheduled: ['confirmed', 'completed', 'cancelled', 'no_show'],
  confirmed: ['completed', 'cancelled', 'no_show'],
  // Reopening, and only backwards to an editable state. Owner and manager only.
  completed: ['confirmed'],
  cancelled: [],
  no_show: [],
};

/** The statuses that still occupy the chair, as the exclusion constraint sees it. */
export const isLive = (status: AppointmentStatus): boolean =>
  status === 'scheduled' || status === 'confirmed';

/**
 * Whether this role may change what the appointment costs (its extra).
 *
 * Mirrors the server's rule 6: anyone who books may adjust a live
 * appointment, only the owner (`appointments:adjust_completed`) a completed
 * one, and nobody a cancelled one or a no-show. It decides what to offer; the
 * API still decides what to allow.
 */
export const mayAdjustPrice = (
  status: AppointmentStatus,
  role: SessionUser['role'] | undefined
): boolean => isLive(status) || (status === 'completed' && role === 'owner');

// The grid a salon day is drawn on unless its appointments need more room.
const DEFAULT_START_MINUTE = 8 * 60;
const DEFAULT_END_MINUTE = 20 * 60;
const DAY_MINUTES = 24 * 60;

export interface PlacedAppointment {
  appointment: Appointment;
  /** Minutes since local midnight. */
  startMinute: number;
  endMinute: number;
  /** Column within its overlap cluster, and how many columns that cluster has. */
  lane: number;
  lanes: number;
}

/** Monday of the week a date falls in. */
export const weekStart = (dateKey: string): string => addDays(dateKey, -weekdayOf(dateKey));

/** The seven date keys of that week, Monday first. */
export const weekDays = (dateKey: string): string[] => {
  const monday = weekStart(dateKey);
  return Array.from({ length: 7 }, (_, index) => addDays(monday, index));
};

/**
 * The 42 date keys a month grid shows: six full weeks, Monday first.
 *
 * Always six rows rather than five-or-six, so switching months does not resize
 * the page under the cursor.
 */
export const monthGrid = (dateKey: string): string[] => {
  const firstOfMonth = `${dateKey.slice(0, 7)}-01`;
  const start = weekStart(firstOfMonth);
  return Array.from({ length: 42 }, (_, index) => addDays(start, index));
};

/** Whether a date key belongs to the same month as the anchor. */
export const isSameMonth = (dateKey: string, anchor: string): boolean =>
  dateKey.slice(0, 7) === anchor.slice(0, 7);

/** The range a calendar request has to cover for a given view. */
export const viewRange = (view: CalendarView, anchor: string): { from: string; to: string } => {
  if (view === 'day') return { from: anchor, to: anchor };
  if (view === 'week') {
    const monday = weekStart(anchor);
    return { from: monday, to: addDays(monday, 6) };
  }
  const grid = monthGrid(anchor);
  // 42 days, comfortably inside the API's 62-day cap.
  return { from: grid[0], to: grid[grid.length - 1] };
};

/** Steps the anchor date by one unit of the current view. */
export const stepAnchor = (view: CalendarView, anchor: string, direction: number): string => {
  if (view === 'day') return addDays(anchor, direction);
  if (view === 'week') return addDays(anchor, 7 * direction);
  const [year, month] = anchor.split('-').map(Number);
  const moved = new Date(Date.UTC(year, month - 1 + direction, 1, 12));
  return moved.toISOString().slice(0, 10);
};

/** Appointments bucketed by the local day they start on. */
export const groupByDay = (
  appointments: Appointment[],
  timeZone?: string
): Record<string, Appointment[]> => {
  const buckets: Record<string, Appointment[]> = {};
  for (const appointment of appointments) {
    const key = zonedDateKey(appointment.startsAt, timeZone);
    (buckets[key] ??= []).push(appointment);
  }
  return buckets;
};

/**
 * Places a day's appointments into side-by-side lanes.
 *
 * Anything that overlaps shares the width of its cluster instead of covering
 * what came before it. That happens whenever a manager double-books on purpose,
 * and whenever the day view is showing every professional at once — and a block
 * hidden behind another one is a client nobody attends.
 */
export const layoutDay = (appointments: Appointment[], timeZone?: string): PlacedAppointment[] => {
  const items = appointments
    .map((appointment) => {
      const startMinute = zonedMinutes(appointment.startsAt, timeZone);
      return {
        appointment,
        startMinute,
        // Measured as elapsed time rather than read off `endsAt`: an
        // appointment running past local midnight would otherwise come back as
        // minute zero and be drawn at the top of the day.
        endMinute: startMinute + minutesBetween(appointment.startsAt, appointment.endsAt),
      };
    })
    .sort((a, b) => a.startMinute - b.startMinute || a.endMinute - b.endMinute);

  const placed: PlacedAppointment[] = [];
  let cluster: Omit<PlacedAppointment, 'lanes'>[] = [];
  let laneEnds: number[] = [];

  const flush = () => {
    if (cluster.length === 0) return;
    const lanes = Math.max(...cluster.map((item) => item.lane)) + 1;
    for (const item of cluster) placed.push({ ...item, lanes });
    cluster = [];
    laneEnds = [];
  };

  for (const item of items) {
    // A gap wider than every open lane ends the cluster: what follows shares
    // its width with nothing behind it.
    if (laneEnds.length > 0 && item.startMinute >= Math.max(...laneEnds)) flush();

    let lane = laneEnds.findIndex((end) => end <= item.startMinute);
    if (lane === -1) lane = laneEnds.length;
    laneEnds[lane] = item.endMinute;
    cluster.push({ ...item, lane });
  }
  flush();

  return placed;
};

/**
 * The vertical span the grid has to draw, in minutes since local midnight.
 *
 * Anchored on a plausible salon day and widened only by what is actually
 * booked, so an early appointment is never cut off and an empty Tuesday does
 * not render twenty-four hours of nothing.
 */
export const gridRange = (
  placed: PlacedAppointment[]
): { startMinute: number; endMinute: number } => {
  let startMinute = DEFAULT_START_MINUTE;
  let endMinute = DEFAULT_END_MINUTE;

  for (const item of placed) {
    startMinute = Math.min(startMinute, Math.floor(item.startMinute / 60) * 60);
    endMinute = Math.max(endMinute, Math.ceil(item.endMinute / 60) * 60);
  }

  return {
    startMinute: Math.max(0, startMinute),
    endMinute: Math.min(DAY_MINUTES, Math.max(endMinute, startMinute + 60)),
  };
};

export interface FreeGap {
  /** Minutes since local midnight. */
  startMinute: number;
  endMinute: number;
}

/**
 * The stretches of a working window nobody is booked into.
 *
 * Cancelled and missed appointments free their time; everything else holds
 * it, completed ones included (that chair *was* busy). `fromMinute` trims the
 * window's start, so today's list does not offer a gap that has already gone.
 * Gaps shorter than `minMinutes` are left out: ten minutes between two clients
 * is not something to book into.
 */
export const freeGaps = (
  appointments: Appointment[],
  window: { startMinute: number; endMinute: number },
  options: { timeZone?: string; minMinutes?: number; fromMinute?: number } = {}
): FreeGap[] => {
  const { timeZone, minMinutes = 30, fromMinute = 0 } = options;
  const busy = appointments
    .filter((appointment) => appointment.status !== 'cancelled' && appointment.status !== 'no_show')
    .map((appointment) => {
      const start = zonedMinutes(appointment.startsAt, timeZone);
      return [start, start + minutesBetween(appointment.startsAt, appointment.endsAt)] as const;
    })
    .sort((a, b) => a[0] - b[0]);

  const gaps: FreeGap[] = [];
  const end = window.endMinute;
  let cursor = Math.max(window.startMinute, fromMinute);
  for (const [start, stop] of busy) {
    if (cursor >= end) break;
    const gapEnd = Math.min(start, end);
    if (gapEnd - cursor >= minMinutes) gaps.push({ startMinute: cursor, endMinute: gapEnd });
    cursor = Math.max(cursor, stop);
  }
  if (end - cursor >= minMinutes) gaps.push({ startMinute: cursor, endMinute: end });
  return gaps;
};

/** The hour marks of a grid range, as minute offsets. */
export const hourMarks = (startMinute: number, endMinute: number): number[] => {
  const marks: number[] = [];
  for (let minute = startMinute; minute <= endMinute; minute += 60) marks.push(minute);
  return marks;
};

/** `540` rendered as `09:00`. */
export const minuteLabel = (minute: number): string => {
  const hours = Math.floor(minute / 60) % 24;
  const minutes = minute % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

/** `90` rendered as `1 h 30 min`. */
export const formatDuration = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours === 0) return `${rest} min`;
  if (rest === 0) return `${hours} h`;
  return `${hours} h ${rest} min`;
};

/** What the toolbar says the calendar is currently showing. */
export const anchorLabel = (view: CalendarView, anchor: string): string => {
  if (view === 'day') {
    return formatKey(anchor, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  }
  if (view === 'week') {
    const days = weekDays(anchor);
    const from = formatKey(days[0], { day: 'numeric', month: 'short' });
    const to = formatKey(days[6], { day: 'numeric', month: 'short', year: 'numeric' });
    return `${from} – ${to}`;
  }
  return formatKey(anchor, { month: 'long', year: 'numeric' });
};

/** The two lines of a week-view column heading. */
export const columnHeading = (dateKey: string): { label: string; sublabel: string } => ({
  label: formatKey(dateKey, { weekday: 'short' }),
  sublabel: formatKey(dateKey, { day: 'numeric', month: 'short' }),
});

/** A one-line summary of what was booked, for a chip or a list row. */
export const servicesSummary = (appointment: Appointment): string =>
  appointment.services
    .map(
      (line) => `${line.serviceName ?? 'Servicio'}${line.quantity > 1 ? ` x${line.quantity}` : ''}`
    )
    .join(' · ');

/** What an appointment would become if its services were swapped for these. */
export interface ServicesChange {
  /** What to send: the lines that stay first, in their order, then the new ones. */
  services: Required<AppointmentServiceInput>[];
  names: string[];
  durationMinutes: number;
  endsAt: string;
  totalAmount: number;
  /** The new total minus today's; negative when she changed down. */
  difference: number;
  /** What she has already paid beyond the new total, to be given back. */
  overpaid: number;
  /** The appointment's discount would be larger than everything left to discount. */
  discountTooLarge: boolean;
  isChanged: boolean;
}

/**
 * Works out a change of services before it is sent, the way the API will.
 *
 * A service that stays keeps the price and the length it was booked at, and its
 * quantity; one that is new takes today's catalogue. Rounded per line and
 * summed afterwards, like `AppointmentService._totals`. The API still decides —
 * this is what lets the dialog show the new total and what has to be given back
 * while she is still making up her mind.
 */
export const servicesChange = (
  appointment: Appointment,
  serviceIds: number[],
  catalogue: Pick<Service, 'id' | 'name' | 'durationMinutes' | 'currentPrice'>[]
): ServicesChange => {
  // By service, summing a service booked on two lines: the dialog picks
  // services, not lines.
  const held = new Map<
    number,
    { name: string; unitPrice: number; durationMinutes: number; quantity: number }
  >();
  for (const line of appointment.services) {
    const known = held.get(line.serviceId);
    if (known) known.quantity += line.quantity;
    else {
      held.set(line.serviceId, {
        name: line.serviceName ?? 'Servicio',
        unitPrice: line.unitPrice,
        durationMinutes: line.durationMinutes,
        quantity: line.quantity,
      });
    }
  }

  const picked = new Set(serviceIds);
  const lines = [...held]
    .filter(([serviceId]) => picked.has(serviceId))
    .map(([serviceId, line]) => ({ serviceId, ...line }));
  for (const serviceId of serviceIds) {
    if (held.has(serviceId)) continue;
    const service = catalogue.find((item) => item.id === serviceId);
    if (!service) continue;
    lines.push({
      serviceId,
      name: service.name,
      unitPrice: service.currentPrice,
      durationMinutes: service.durationMinutes,
      quantity: 1,
    });
  }

  const subtotal = round(
    lines.reduce((sum, line) => sum + round(line.unitPrice * line.quantity), 0)
  );
  const durationMinutes = lines.reduce(
    (sum, line) => sum + line.durationMinutes * line.quantity,
    0
  );
  const totalAmount = round(subtotal + appointment.extraAmount - appointment.discountAmount);
  const basket = (items: { serviceId: number; quantity: number }[]) =>
    items
      .map((item) => `${item.serviceId}x${item.quantity}`)
      .sort()
      .join(',');

  return {
    services: lines.map(({ serviceId, quantity }) => ({ serviceId, quantity })),
    names: lines.map((line) => `${line.name}${line.quantity > 1 ? ` x${line.quantity}` : ''}`),
    durationMinutes,
    endsAt: new Date(Date.parse(appointment.startsAt) + durationMinutes * 60_000).toISOString(),
    totalAmount,
    difference: round(totalAmount - appointment.totalAmount),
    overpaid: Math.max(0, round(appointment.paidAmount - appointment.refundedAmount - totalAmount)),
    discountTooLarge: appointment.discountAmount > round(subtotal + appointment.extraAmount),
    isChanged:
      basket(lines) !==
      basket([...held].map(([serviceId, line]) => ({ serviceId, quantity: line.quantity }))),
  };
};
